<script setup>
  import { ref, computed, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import moment from "moment";
  import ListContainer from "@/components/episto-ui/list/ListContainer.vue";
  import ListItem from "@/components/episto-ui/list/ListItem.vue";
  import { Input } from "@/components/ui/input";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Search, Plus, CircleAlert } from "lucide-vue-next";
  import { useAiInsightConversationsStore } from "@stores/survey/assistant/aiInsightConversations";

  const aiInsightConversationsStore = useAiInsightConversationsStore();
  const { conversations } = storeToRefs(aiInsightConversationsStore);
  const {
    newConversation,
    selectConversation,
    deleteConversation,
    updateConversation,
    updateConversationTitle,
    restoreConversationTitle,
    isConversationSelected,
  } = aiInsightConversationsStore;

  const search = ref("");

  const matchesSearch = (conversation) => {
    const query = search.value.toLowerCase().trim();
    if (!query) return true;
    return (conversation.title || "").toLowerCase().includes(query);
  };

  // Only saved conversations are listed. A brand-new unsaved thread (id === null) is
  // surfaced as the empty state in the central panel, not as a list row — so the
  // list iterates the full store array (to keep indices aligned with the store's
  // index-based API) and hides the rest via ListItem's `visible` prop.
  const isVisible = (conversation) => conversation.id !== null && matchesSearch(conversation);
  const visibleCount = computed(() => conversations.value.filter(isVisible).length);

  const isDefaultConversation = (conversation) => Boolean(conversation.exec_summary || conversation.objective_summary);
  const formatDate = (date) => (date ? moment(date).format("MMM D, YYYY") : "");

  onMounted(async () => {
    if (conversations.value.length === 0) await newConversation();
    else selectConversation(0);
  });
</script>

<template>
  <ListContainer element-name="chat">
    <template #header-action>
      <Input
        v-model="search"
        :icon="Search"
        placeholder="Search..."
        clearable
        class="w-full" />
    </template>

    <template #elements>
      <div
        v-if="visibleCount === 0"
        class="flex items-center justify-center py-8 text-sm text-muted-foreground">
        {{ search ? "No results" : "No conversations yet" }}
      </div>
      <ListItem
        v-for="(conversation, index) in conversations"
        :key="`assistant_thread_${index}`"
        :index="index"
        :visible="isVisible(conversation)"
        :element-title="conversation.title || 'Untitled'"
        :select-element="selectConversation"
        :delete-element="deleteConversation"
        :confirm-deletion="true"
        :is-element-selected="isConversationSelected"
        :edit-element-title="updateConversationTitle"
        :save-title-changes="updateConversation"
        :restore-title-changes="restoreConversationTitle">
        <!-- Inline indicator for conversations generated before data collection ended -->
        <template #details>
          <Tooltip
            v-if="conversation.is_created_before_complete"
            content="Generated before the end of the data collection period">
            <CircleAlert class="h-4 w-4 shrink-0 text-muted-foreground" />
          </Tooltip>
        </template>

        <!-- Second row: creation date + a Default badge for auto-generated conversations -->
        <template #additional-content>
          <div class="flex items-center gap-2 -mt-2.5">
            <span class="text-xs text-muted-foreground">{{ formatDate(conversation.created_at) }}</span>
            <Badge
              v-if="isDefaultConversation(conversation)"
              variant="secondary"
              class="text-3xs font-medium">
              Default
            </Badge>
          </div>
        </template>
      </ListItem>
    </template>

    <template #footer>
      <Button
        variant="outline"
        class="w-full shadow-xs"
        data-cy="assistant-new-thread"
        @click="newConversation">
        <Plus />
        New chat
      </Button>
    </template>
  </ListContainer>
</template>
